import { useState } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useMutation } from "@tanstack/react-query";
import { useAuthenticatedMutationAsync } from "../lib/api";
import type { RQLError } from "../lib/rql";
import { tokenize, parse, evaluate, RQL_FIELD_DEFINITIONS } from "../lib/rql";
import RQLQueryInput from "./RQLQueryInput";
import ActionButton from "../components/ActionButton";
import {
  REBALANCE_BOARDS_PATH,
  useRebalanceBoards,
  type RebalanceBoard,
} from "./boards";

interface BoardManagementModalProps {
  show: boolean;
  onHide: () => void;
  onBoardCreated?: (board: RebalanceBoard) => void;
  onBoardUpdated?: (board: RebalanceBoard) => void;
  onBoardDeleted?: (boardId: number) => void;
}

const BoardManagementModal = ({
  show,
  onHide,
  onBoardCreated,
  onBoardUpdated,
  onBoardDeleted,
}: BoardManagementModalProps) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createQuery, setCreateQuery] = useState("");
  const [editingBoard, setEditingBoard] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editQuery, setEditQuery] = useState("");
  const [deletingBoard, setDeletingBoard] = useState<number | null>(null);

  // Every board, walked page by page from `GET /v1/rebalances/boards`.
  const {
    boards,
    isPending: isLoadingBoards,
    reload: reloadBoards,
  } = useRebalanceBoards();

  // Validate RQL queries
  const validateQuery = (query: string): RQLError | null => {
    if (!query.trim()) return null;

    try {
      const tokens = tokenize(query);
      const ast = parse(tokens);
      evaluate(ast, [], RQL_FIELD_DEFINITIONS);
      return null;
    } catch (error) {
      return {
        name: "ValidationError",
        message: error instanceof Error ? error.message : "Invalid query",
        position: undefined,
      };
    }
  };

  const createError = validateQuery(createQuery);
  const editError = validateQuery(editQuery);

  // Create board mutation
  const createBoardMutation = useAuthenticatedMutationAsync<{
    data: RebalanceBoard;
  }>(REBALANCE_BOARDS_PATH, async () => ({
    method: "POST",
    body: JSON.stringify({ name: createName, query: createQuery }),
  }));

  // Update board mutation
  const updateBoardMutation = useAuthenticatedMutationAsync<{
    data: RebalanceBoard;
  }>(
    editingBoard ? `${REBALANCE_BOARDS_PATH}/${editingBoard}` : "",
    async () => ({
      method: "PUT",
      body: JSON.stringify({ name: editName, query: editQuery }),
    }),
  );

  // Delete board mutation
  const deleteBoardMutation = useAuthenticatedMutationAsync<{ data: any }>(
    deletingBoard ? `${REBALANCE_BOARDS_PATH}/${deletingBoard}` : "",
    async () => ({
      method: "DELETE",
    }),
  );

  // Wrap in useMutation for proper state management
  const createMutation = useMutation({
    mutationFn: createBoardMutation,
  });

  const updateMutation = useMutation({
    mutationFn: updateBoardMutation,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteBoardMutation,
  });

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createName.trim() || createError) return;

    try {
      const result = await createMutation.mutateAsync(undefined);
      const newBoard = result.data;
      reloadBoards();
      onBoardCreated?.(newBoard);
      setCreateName("");
      setCreateQuery("");
      setShowCreateForm(false);
    } catch {
      // Error is surfaced to the user via the createMutation.error Alert.
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBoard || !editName.trim() || editError) return;

    try {
      const result = await updateMutation.mutateAsync(undefined);
      const updatedBoard = result.data;
      reloadBoards();
      onBoardUpdated?.(updatedBoard);
      setEditingBoard(null);
      setEditName("");
      setEditQuery("");
    } catch {
      // Error is surfaced to the user via the updateMutation.error Alert.
    }
  };

  const handleDeleteBoard = async (boardId: number) => {
    try {
      setDeletingBoard(boardId);

      await deleteMutation.mutateAsync(undefined);
      reloadBoards();
      onBoardDeleted?.(boardId);
      setDeletingBoard(null);
    } catch {
      setDeletingBoard(null);
    }
  };

  const handleEditBoard = (board: RebalanceBoard) => {
    setEditingBoard(board.id);
    setEditName(board.name);
    setEditQuery(board.query);
  };

  const handleCancelEdit = () => {
    setEditingBoard(null);
    setEditName("");
    setEditQuery("");
  };

  const handleClose = () => {
    setShowCreateForm(false);
    setCreateName("");
    setCreateQuery("");
    setEditingBoard(null);
    setEditName("");
    setEditQuery("");
    setDeletingBoard(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <div className="d-flex justify-content-between align-items-center w-100">
          <Modal.Title>Boards</Modal.Title>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="me-3"
          >
            Add Board
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body>
        {/* Create New Board Section - Only shown when showCreateForm is true */}
        {showCreateForm && (
          <div className="mb-4">
            <h5>Add Board</h5>
            <Form onSubmit={handleCreateSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Board Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={createName}
                      onChange={(e) => setCreateName(e.target.value)}
                      placeholder="Enter board name"
                      maxLength={100}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>RQL Query</Form.Label>
                    <RQLQueryInput
                      query={createQuery}
                      onChange={setCreateQuery}
                      error={createError}
                      showLabel={false}
                      className=""
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={
                      !createName.trim() ||
                      !!createError ||
                      createMutation.isPending
                    }
                    className="me-2"
                  >
                    {createMutation.isPending ? "Creating..." : "Create Board"}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
              {createMutation.error && (
                <Alert variant="danger" className="mt-3">
                  Error creating board: {createMutation.error.message}
                </Alert>
              )}
            </Form>
          </div>
        )}

        {/* Existing Boards List */}
        <div>
          {isLoadingBoards ? (
            <div>Loading boards...</div>
          ) : boards.length === 0 ? (
            <Alert variant="info">
              No boards found. Create your first board above.
            </Alert>
          ) : (
            <>
              {boards.map((board, index) => (
                <div
                  key={board.id}
                  className="py-2 px-3 mb-2"
                  style={{
                    borderBottom:
                      index === boards.length - 1
                        ? "none"
                        : "1px solid var(--bs-border-color)",
                  }}
                >
                  {editingBoard === board.id ? (
                    // Edit mode
                    <Form onSubmit={handleEditSubmit}>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Board Name</Form.Label>
                            <Form.Control
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              placeholder="Enter board name"
                              maxLength={100}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>RQL Query</Form.Label>
                            <RQLQueryInput
                              query={editQuery}
                              onChange={setEditQuery}
                              error={editError}
                              showLabel={false}
                              className=""
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <div>
                        <Button
                          type="submit"
                          variant="primary"
                          size="sm"
                          disabled={
                            !editName.trim() ||
                            !!editError ||
                            updateMutation.isPending
                          }
                          className="me-2"
                        >
                          {updateMutation.isPending ? "Saving..." : "Save"}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                      </div>
                      {updateMutation.error && (
                        <Alert variant="danger" className="mt-2">
                          Error updating board: {updateMutation.error.message}
                        </Alert>
                      )}
                    </Form>
                  ) : (
                    // Display mode
                    <>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1 me-3">
                          <h6 className="mb-1">{board.name}</h6>
                          <small className="text-muted">
                            {board.query || "(empty)"}
                          </small>
                        </div>
                        <div className="d-flex flex-shrink-0">
                          <ActionButton
                            variant="icon"
                            icon="/icons/edit.svg"
                            label="Edit"
                            onClick={() => handleEditBoard(board)}
                            className="me-2"
                          />
                          <ActionButton
                            variant="icon"
                            icon="/icons/trash.svg"
                            label="Delete"
                            onClick={() => handleDeleteBoard(board.id)}
                            disabled={deletingBoard === board.id}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "none" }}>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BoardManagementModal;
